import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useAccountEnv, isApiError } from '../client'
import type { Visit, ExtendResult } from '../types'
import { mapsUrl, formatRange, formatDuration, formatAmount, formatClock, formatUsd, estimateExtendUsd } from '../primitives'
import { ChevDown } from '../icons'

export function HistoryList({
  visits, hasPlates, focusSession, email,
}: {
  visits: Visit[]
  hasPlates: boolean
  focusSession?: string | null
  email: string | null
}) {
  const { client } = useAccountEnv()
  // Fetched once for the whole list (not per-row) — only a live session can
  // extend, so in practice at most one row ever needs this, but a single fetch
  // is correct either way. null = still loading; the "+30/+60" buttons render
  // normally until we've actually confirmed there's no card, so a slow network
  // call never blocks or flickers the common (has-a-card) case.
  const [hasCard, setHasCard] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const pm = await client.fetchPaymentMethod()
        if (!cancelled) setHasCard(!!pm.last4)
      } catch { /* unknown is treated the same as "has a card" — never block on this */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (visits.length === 0) {
    return (
      <div style={{
        background: 'var(--color-gray-100)', border: '1.5px dashed var(--pk-brand-mid)',
        borderRadius: 'var(--radius-md)', padding: 18, textAlign: 'center',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)', marginBottom: 3 }}>No sessions yet</div>
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500 }}>
          {hasPlates
            ? 'Receipts land here after your first parking session.'
            : 'Add a license plate in the Account tab and your sessions will show up here automatically.'}
        </div>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {visits.map(v => <HistoryRow key={v.id} visit={v} focusSession={focusSession} email={email} hasCard={hasCard} />)}
    </div>
  )
}

function HistoryRow({
  visit, focusSession, email, hasCard,
}: {
  visit: Visit
  focusSession?: string | null
  email: string | null
  hasCard: boolean | null
}) {
  const { client } = useAccountEnv()
  const txnId = visit.id.startsWith('parkify:') ? visit.id.slice('parkify:'.length) : null
  const canExtend = !visit.ended_at && visit.source === 'parkify' && !!txnId
  const isFocused = !!txnId && !!focusSession && focusSession === txnId

  const [expanded, setExpanded] = useState<boolean>(isFocused)
  const rowRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (isFocused && rowRef.current) rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [isFocused])

  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [emailing, setEmailing] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  // ── extend (add time) — unchanged money flow, quote → confirm → charge ──
  const [extending, setExtending] = useState(false)
  const [extendError, setExtendError] = useState<string | null>(null)
  const [extended, setExtended] = useState<ExtendResult | null>(null)
  const [pending, setPending] = useState<{ minutes: number; usd: number | null; quoted: boolean } | null>(null)
  const [quoting, setQuoting] = useState<number | null>(null)
  const inFlight = useRef(false)
  const idemKey = useRef<string>(crypto.randomUUID())

  const onPickIncrement = async (minutes: number) => {
    if (quoting != null || inFlight.current || !txnId) return
    setExtendError(null)
    setQuoting(minutes)
    try {
      const q = await client.extendQuote({ txnId, additionalMinutes: minutes })
      setPending({ minutes, usd: q.quoteUsd, quoted: true })
    } catch {
      setPending({ minutes, usd: estimateExtendUsd(visit, minutes), quoted: false })
    } finally {
      setQuoting(null)
    }
  }

  const onExtend = async (minutes: number) => {
    if (inFlight.current || !txnId) return
    inFlight.current = true
    setExtending(true)
    setExtendError(null)
    try {
      const res = await client.extendSession({ txnId, additionalMinutes: minutes, idempotencyKey: idemKey.current })
      setExtended(res)
      setPending(null)
      idemKey.current = crypto.randomUUID()
    } catch (e) {
      setExtendError(isApiError(e) ? e.message : 'Could not add time.')
    } finally {
      inFlight.current = false
      setExtending(false)
    }
  }

  const onDownload = async () => {
    if (downloading) return
    setDownloading(true)
    setDownloadError(null)
    try {
      const blob = await client.fetchReceipt(visit.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      a.download = `parkify-receipt-${visit.id.replace(':', '-')}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1500)
    } catch (e) {
      setDownloadError(isApiError(e) ? e.message : 'Could not download receipt.')
    } finally {
      setDownloading(false)
    }
  }

  const onEmail = async () => {
    if (emailing || emailSent) return
    setEmailing(true)
    setEmailError(null)
    try {
      await client.emailReceipt(visit.id)
      setEmailSent(true)
    } catch (e) {
      setEmailError(isApiError(e) ? e.message : 'Could not email receipt.')
    } finally {
      setEmailing(false)
    }
  }

  const isLive = !visit.ended_at

  return (
    <div ref={rowRef} style={{
      background: 'var(--color-white)',
      border: `1px solid ${expanded || isFocused ? 'var(--color-brand)' : 'var(--color-gray-300)'}`,
      borderRadius: 'var(--radius-md)',
      boxShadow: isFocused ? '0 0 0 3px var(--color-brand-light)' : undefined,
      scrollMarginTop: 16,
    }}>
      <button type="button" onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '13px 15px' }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {visit.zone.name || 'Unknown lot'}
            </span>
            <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-brand)', flexShrink: 0 }}>
              {formatAmount(visit.amount_cents)}
            </span>
          </span>
          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatRange(visit.started_at, visit.ended_at)}
              {visit.duration_minutes != null && ` · ${formatDuration(visit.duration_minutes)}`}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {isLive && (
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--color-info)' }}>In progress</span>
              )}
              {visit.has_refunds && (
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--color-warning)' }}>Refunded</span>
              )}
            </span>
          </span>
        </span>
        <ChevDown open={expanded} />
      </button>

      {expanded && (
        <div className="pka-fadein" style={{ padding: '0 15px 14px' }}>
          <div style={{ paddingTop: 12, borderTop: '1px solid var(--color-gray-200)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 8 }}>
              Receipt
            </div>
            {visit.plate && <ReceiptLine label="Plate" value={visit.plate.toUpperCase()} />}
            {visit.zone.address && (
              <ReceiptLine label="Location" value={
                <a href={mapsUrl(visit.zone.address)} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-brand)', textAlign: 'right' }}>
                  {visit.zone.address}
                </a>
              } />
            )}
            {/* Reservation ID. The post-payment receipt tells the parker to keep
                this ("quote it to support to find this session fast") and support's
                own lookup keys on it — but History, the one place they'd come back
                to for it, never showed it, so the only surviving copy was the
                confirmation SMS or email. Same label and mono treatment the
                checkout receipt uses (SessionSummaryCard), so the two read as the
                same field rather than two ids.

                Only for `parkify:` rows: `txnId` is null for Mitte-mirrored visits,
                which have no Parkify reservation to quote. */}
            {txnId && (
              <ReceiptLine label="Reservation ID" value={
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-black)', textAlign: 'right', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
                  {txnId}
                </span>
              } />
            )}
            {visit.base_cents != null ? (
              // Native checkout stored the real FTC split — itemize it. The
              // fee lines sum to the charged total; a refund (if any) then
              // brings it to the net Total below.
              <>
                <ReceiptLine label="Parking" value={formatAmount(visit.base_cents)} />
                {visit.admin_fee_cents != null && visit.admin_fee_cents > 0 && (
                  <ReceiptLine label="Admin fee" value={formatAmount(visit.admin_fee_cents)} />
                )}
                {visit.service_fee_cents != null && visit.service_fee_cents > 0 && (
                  <ReceiptLine label="Service fee" value={formatAmount(visit.service_fee_cents)} />
                )}
                {visit.has_refunds && visit.refunded_cents > 0 && (
                  <ReceiptLine label="Refunded" value={`−${formatAmount(visit.refunded_cents)}`} valueColor="var(--color-success)" />
                )}
              </>
            ) : (
              // No stored split (legacy / Mitte-mirrored) — show the all-in
              // amount; the itemized breakdown lives in the receipt PDF.
              <>
                <ReceiptLine label="Amount paid" value={formatAmount(visit.amount_cents)} />
                {visit.has_refunds && visit.refunded_cents > 0 && (
                  <>
                    <ReceiptLine label="Original charge" value={formatAmount(visit.gross_cents)} />
                    <ReceiptLine label="Refunded" value={`−${formatAmount(visit.refunded_cents)}`} valueColor="var(--color-success)" />
                  </>
                )}
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 6, borderTop: '1px solid var(--color-gray-200)' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-black)' }}>Total</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--color-black)' }}>{formatAmount(visit.amount_cents)}</span>
            </div>
            {visit.base_cents == null && (
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
                Your full itemized receipt — parking, fees, and any credits — is in the receipt PDF below.
              </p>
            )}
          </div>

          {canExtend && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-gray-200)' }}>
              {extended ? (
                <div style={{ fontSize: 12, color: 'var(--color-brand)', fontWeight: 600 }}>
                  ✓ Added {extended.addedMinutes} min
                  {extended.chargedCents > 0 ? ` · charged ${formatAmount(extended.chargedCents)}` : ' · no charge'}
                  {' '}· now until {formatClock(extended.newExpiresAt)}
                </div>
              ) : pending ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-black)' }}>
                    Add {formatDuration(pending.minutes)}
                    {pending.usd != null && (
                      <span style={{ color: 'var(--color-brand)' }}>
                        {' · '}{pending.quoted ? '' : 'about '}{formatUsd(pending.usd)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
                    {pending.quoted
                      ? "This lot's exact price for the added time. We'll charge your card on file and add it right away."
                      : pending.usd != null
                        ? "Estimated from this session's rate. We'll charge your card on file and show the exact amount once the time's added."
                        : "We'll charge your card on file at this lot's rate and show the exact amount once the time's added."}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => { setPending(null); setExtendError(null) }} disabled={extending}
                      className="pka-btn-text" style={{ fontSize: 12, padding: '4px 12px' }}>Cancel</button>
                    <button type="button" onClick={() => onExtend(pending.minutes)} disabled={extending}
                      className="pka-btn-primary" style={{ fontSize: 12, padding: '6px 14px', fontWeight: 700 }}>
                      {extending ? 'Adding…' : pending.usd != null ? `Confirm · ${formatUsd(pending.usd)}` : 'Confirm & add time'}
                    </button>
                  </div>
                </div>
              ) : hasCard === false ? (
                <div style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>
                  Add a card in the Account tab before adding time to this session.
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>Need more time? Add it to your active session.</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[30, 60].map(min => (
                      <button key={min} type="button" onClick={() => onPickIncrement(min)} disabled={quoting != null}
                        className="pka-btn-text" style={{ fontSize: 12, padding: '4px 12px', fontWeight: 700 }}>
                        {quoting === min ? '…' : `+${min === 60 ? '1 hr' : `${min} min`}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {extendError && <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 6 }}>{extendError}</div>}
            </div>
          )}

          {visit.receipt_available && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
              {emailSent ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-success)' }}>
                  {email ? `Sent to ${email} ✓` : 'Receipt emailed ✓'}
                </span>
              ) : (
                <button type="button" onClick={onEmail} disabled={emailing}
                  style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-brand)' }}>
                  {emailing ? 'Sending…' : 'Email receipt →'}
                </button>
              )}
              <button type="button" onClick={onDownload} disabled={downloading}
                style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-brand)' }}>
                {downloading ? 'Generating…' : 'Download receipt'}
              </button>
            </div>
          )}
          {(downloadError || emailError) && (
            <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 8, textAlign: 'center' }}>
              {downloadError || emailError}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ReceiptLine({ label, value, valueColor }: { label: string; value: ReactNode; valueColor?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
      <span style={{ fontSize: 13, color: 'var(--color-gray-600)', fontWeight: 500, flexShrink: 0 }}>{label}</span>
      {typeof value === 'string'
        ? <span style={{ fontSize: 13, fontWeight: 600, color: valueColor || 'var(--color-black)', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{value}</span>
        : value}
    </div>
  )
}
